/**
 * Left rail for the Settings area (round9/design-pass — Direction A).
 *
 * Rendered by SettingsPage (three in-page sections) and EmbedPage (its own
 * route), so the rail is the one constant across every configuration
 * surface. Desktop: a 232px column beside the content. Under 900px it
 * becomes a horizontally scrollable strip above the content, so every
 * section stays one tap away on a phone rather than pushing the form
 * below the fold.
 *
 * Every item is a real link (deep-linkable, Back works). aria-current marks
 * the active one, and the active state is carried by a background change,
 * not colour alone.
 */
import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { SETTINGS_SECTIONS, type SettingsSectionKey } from "./settingsSections";
import styles from "./SettingsRail.module.css";

export function SettingsRail({ active }: { active: SettingsSectionKey }) {
  return (
    <nav aria-label="Settings sections" className={styles.rail}>
      <div className={styles.railLabel} aria-hidden="true">Settings</div>
      <ul className={styles.list}>
        {SETTINGS_SECTIONS.map((section) => {
          const isActive = section.key === active;
          return (
            <li key={section.key}>
              <Link
                to={section.path}
                className={isActive ? styles.itemActive : styles.item}
                aria-current={isActive ? "page" : undefined}
              >
                {section.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** Rail + content side by side; stacks under 900px. */
export function SettingsLayout({ active, children }: { active: SettingsSectionKey; children: ReactNode }) {
  return (
    <div className={styles.layout}>
      <SettingsRail active={active} />
      <div className={styles.content}>{children}</div>
    </div>
  );
}
